/**
 * @module components/common/TopNFieldGroup
 * @description TopN 配置公共 UI（动态取值开关 + 数量 + 显示其它）
 *
 * 纯展示组件，状态由父级配置项 hook 注入。
 */
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Bubble, Input, InputAdornment, Select, Switch } from 'tea-component';
import './index.scss';

export interface ITopNFieldGroupValue {
  count: number;
  dynamicState: boolean;
  showOthers: string;
  onCountChange: (count: number) => void;
  onDynamicStateChange: (enabled: boolean) => void;
  onShowOthersChange: (value: string) => void;
}

export interface ITopNFieldGroupProps extends ITopNFieldGroupValue {
  /** 数量输入超过上限时的提示 */
  countLimitTip?: string;
  /** 数量上限，默认 100 */
  countMax?: number;
  className?: string;
}

const TopNFieldGroup: React.FC<ITopNFieldGroupProps> = ({
  count,
  dynamicState,
  showOthers,
  onCountChange,
  onDynamicStateChange,
  onShowOthersChange,
  countLimitTip,
  countMax = 100,
  className,
}) => {
  const { formatMessage } = useIntl();

  const showOthersValue = showOthers || 'and others';
  const isCountOverLimit = count > countMax;

  const hintText = useMemo(
    () =>
      showOthersValue === 'only'
        ? formatMessage({ id: 'dbl_myAnalysis_top' })
        : formatMessage({ id: 'dbl_myAnalysis_top_others' }),
    [showOthersValue, formatMessage],
  );

  const showOthersOptions = useMemo(
    () => [
      { value: 'and others', text: formatMessage({ id: 'dbl_chart_and_others' }) },
      { value: 'only', text: formatMessage({ id: 'dbl_chart_only' }) },
    ],
    [formatMessage],
  );

  const handleCountInput = (value: string): void => {
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0 || !Number.isInteger(num)) {
      return;
    }
    onCountChange(num);
  };

  return (
    <div className={classNames('dbl-topn-field-group', className)}>
      <div className="dbl-topn-field-group__dynamic-row">
        <span>{formatMessage({ id: 'dbl_chart_dynamic_series' })}</span>
        <Switch value={dynamicState} onChange={onDynamicStateChange} />
      </div>

      {dynamicState && (
        <div className="dbl-topn-field-group__panel">
          <div className="dbl-topn-field-group__panel-row">
            <Bubble
              error
              visible={isCountOverLimit}
              placement="top"
              content={countLimitTip}
            >
              <InputAdornment
                before={formatMessage({ id: 'dbl_chart_top' })}
              >
                <Input
                  type="number"
                  size="s"
                  value={String(count)}
                  step={1}
                  style={{
                    width: 85,
                    borderColor: isCountOverLimit
                      ? '#ef5858'
                      : count
                        ? undefined
                        : '#326ff0',
                  }}
                  onChange={handleCountInput}
                />
              </InputAdornment>
            </Bubble>
            <Select
              className="dbl-topn-field-group__show-others"
              appearance="button"
              matchButtonWidth
              listWidth={180}
              value={showOthersValue}
              options={showOthersOptions}
              onChange={onShowOthersChange}
            />
          </div>
          <p className="dbl-topn-field-group__hint">{hintText}</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(TopNFieldGroup);
